import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/store/hooks';
import {
  fetchWebsiteBlogs,
  setBlogsQuery,
  updateWebsiteBlog,
  deleteWebsiteBlog,
  bulkDeleteWebsiteBlogs,
  toggleBlogSelected,
  setBlogsSelected,
} from '../../redux/features/websiteSlice';
import { useBlogCategories } from '../../services/httpServices/queries';
import type { BlogTaxonomyNode } from '../../services/httpServices/websiteService';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { toast } from 'sonner';
import { ENV } from '../../config/env';
import {
  Newspaper,
  Loader2,
  AlertCircle,
  Plus,
  Pencil,
  Trash2,
  Search,
  X,
  Power,
  PowerOff,
  FolderTree,
  ExternalLink,
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Draft' },
];

function formatDate(value?: string) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-GB', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function flattenCategories(nodes: BlogTaxonomyNode[]): BlogTaxonomyNode[] {
  const out: BlogTaxonomyNode[] = [];
  const walk = (list: BlogTaxonomyNode[]) => {
    for (const node of list) {
      out.push(node);
      walk(node.children);
    }
  };
  walk(nodes);
  return out;
}

export function WebsiteBlogs() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { items: blogs, meta, loading, error } = useAppSelector((state) => state.website.blogs);
  const query = useAppSelector((state) => state.website.blogsQuery);
  const selectedIds = useAppSelector((state) => state.website.selectedBlogs);

  const { data: categoriesData } = useBlogCategories();
  const categoryOptions = flattenCategories(categoriesData?.data ?? []);

  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [deletingOne, setDeletingOne] = useState(false);

  // Hydrate the query from the URL once so filters/pagination are deep-linkable.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    dispatch(
      setBlogsQuery({
        page: Math.max(1, parseInt(params.get('page') || '1', 10)),
        search: params.get('search') || '',
        status: params.get('status') || '',
        categoryId: params.get('categoryId') || '',
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the URL in sync with the Redux query state.
  useEffect(() => {
    const next = new URLSearchParams();
    if (query.page > 1) next.set('page', String(query.page));
    if (query.search) next.set('search', query.search);
    if (query.status) next.set('status', query.status);
    if (query.categoryId) next.set('categoryId', query.categoryId);
    navigate(`${location.pathname}${next.toString() ? `?${next}` : ''}`, { replace: true });
  }, [query.page, query.search, query.status, query.categoryId, navigate, location.pathname]);

  // API-based fetch (server does filtering + pagination; Redis caches the response).
  useEffect(() => {
    const timer = setTimeout(() => dispatch(fetchWebsiteBlogs(query)), 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, query.page, query.limit, query.search, query.status, query.categoryId]);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    const visible = new Set(blogs.map((post) => post.id));
    const filtered = selectedIds.filter((id) => visible.has(id));
    if (filtered.length !== selectedIds.length) dispatch(setBlogsSelected(filtered));
  }, [blogs, selectedIds, dispatch]);

  const allSelected = blogs.length > 0 && blogs.every((post) => selectedIds.includes(post.id));
  const someSelected = blogs.some((post) => selectedIds.includes(post.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    const everySelected = blogs.length > 0 && blogs.every((post) => selectedIds.includes(post.id));
    dispatch(setBlogsSelected(everySelected ? [] : blogs.map((post) => post.id)));
  };

  const handleTogglePublished = async (id: string, isPublished: boolean) => {
    try {
      await dispatch(updateWebsiteBlog({ id, data: { isPublished: !isPublished } })).unwrap();
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to update blog post');
    }
  };

  const handleDeleteOne = async () => {
    if (!deleteTarget) return;
    setDeletingOne(true);
    try {
      await dispatch(deleteWebsiteBlog(deleteTarget.id)).unwrap();
      toast.success('Blog post deleted');
      dispatch(fetchWebsiteBlogs(query));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to delete blog post');
    } finally {
      setDeletingOne(false);
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    setBulkDeleting(true);
    try {
      await dispatch(bulkDeleteWebsiteBlogs(ids)).unwrap();
      toast.success(`Deleted ${ids.length} post${ids.length === 1 ? '' : 's'}`);
      dispatch(fetchWebsiteBlogs(query));
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to delete selected blog posts');
    } finally {
      setBulkDeleting(false);
      setBulkConfirmOpen(false);
    }
  };

  const hasFilters = !!query.search || !!query.status || !!query.categoryId;
  const isInitialLoading = loading && blogs.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Newspaper className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-text">Website Blog</h1>
            <p className="text-sm text-text-muted">Articles shown on the public /blog page.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/website/taxonomy')}
            className="px-4 py-2 border border-border text-text rounded-lg hover:border-primary hover:text-primary transition-colors text-sm font-medium flex items-center gap-2"
          >
            <FolderTree size={16} />
            Categories &amp; Tags
          </button>
          <button
            onClick={() => navigate('/website/blogs/new')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            Add Post
          </button>
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search blog posts..."
              value={query.search}
              onChange={(e) => dispatch(setBlogsQuery({ search: e.target.value, page: 1 }))}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {query.search && (
              <button
                onClick={() => dispatch(setBlogsQuery({ search: '', page: 1 }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-danger"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <select
            value={query.categoryId}
            onChange={(e) =>
              dispatch(setBlogsQuery({ categoryId: e.target.value, page: 1 }))
            }
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 sm:w-52"
          >
            <option value="">All categories</option>
            {categoryOptions.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          <select
            value={query.status}
            onChange={(e) => dispatch(setBlogsQuery({ status: e.target.value, page: 1 }))}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 sm:w-44"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.length}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleting}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete
            </button>
            <button
              type="button"
              onClick={() => dispatch(setBlogsSelected([]))}
              disabled={bulkDeleting}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {isInitialLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : error && blogs.length === 0 ? (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          {loading && (
            <div className="flex items-center gap-2 px-4 py-2 border-b border-border text-xs text-text-muted">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Updating…
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={blogs.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Post</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Author</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {blogs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-text-muted">
                      {hasFilters ? (
                        <span>
                          No blog posts match your filters.{' '}
                          <button
                            onClick={() =>
                              dispatch(
                                setBlogsQuery({ search: '', status: '', categoryId: '', page: 1 }),
                              )
                            }
                            className="text-primary hover:underline"
                          >
                            Clear filters
                          </button>
                        </span>
                      ) : (
                        'No blog posts yet. Add your first post to get started.'
                      )}
                    </td>
                  </tr>
                ) : (
                  blogs.map((post) => (
                    <tr key={post.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(post.id)}
                          onChange={() => dispatch(toggleBlogSelected(post.id))}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {post.image ? (
                            <img
                              src={post.image}
                              alt=""
                              className="w-12 h-9 rounded-lg object-cover bg-background border border-border flex-shrink-0"
                            />
                          ) : (
                            <span className="w-12 h-9 rounded-lg bg-background border border-border" />
                          )}
                          <button
                            onClick={() => navigate(`/website/blogs/${post.id}/edit`)}
                            className="text-sm font-medium text-text hover:text-primary text-left truncate block max-w-[320px]"
                          >
                            {post.title}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {post.categories?.length
                          ? post.categories.map((category) => category.name).join(', ')
                          : post.category || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">{post.author}</td>
                      <td className="px-4 py-3 text-sm text-text-muted">{formatDate(post.publishedAt)}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            post.isPublished ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                          }`}
                        >
                          {post.isPublished ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleTogglePublished(post.id, post.isPublished)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                            title={post.isPublished ? 'Unpublish' : 'Publish'}
                          >
                            {post.isPublished ? <PowerOff size={16} /> : <Power size={16} />}
                          </button>
                          <button
                            onClick={() =>
                              window.open(
                                `${ENV.VITE_WEBSITE_URL.replace(/\/$/, '')}/blog/${post.slug}`,
                                '_blank',
                                'noopener,noreferrer',
                              )
                            }
                            disabled={!post.slug || !post.isPublished}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title={post.isPublished ? 'Preview on website' : 'Publish the post to preview it'}
                          >
                            <ExternalLink size={16} />
                          </button>
                          <button
                            onClick={() => navigate(`/website/blogs/${post.id}/edit`)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget({ id: post.id, title: post.title })}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={query.page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={query.limit}
            onPageChange={(page) => dispatch(setBlogsQuery({ page }))}
            itemName="posts"
          />
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete blog post?"
        description={
          <>
            This will delete <strong className="text-text">{deleteTarget?.title}</strong> from the
            website. This cannot be undone.
          </>
        }
        confirmLabel="Delete post"
        loading={deletingOne}
        onConfirm={handleDeleteOne}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.length} post${selectedIds.length === 1 ? '' : 's'}?`}
        description="This action will remove the selected blog posts. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.length} post${selectedIds.length === 1 ? '' : 's'}`}
        loading={bulkDeleting}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
